import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, Download, LoaderCircle } from "lucide-react";
import DiscoverySelect from "./DiscoverySelect";
import { resultStandings } from "./completedResults";
import { matchDateLabel, matchLocation } from "./matchDiscovery";
import "./LiveStandingsExport.css";

export function liveStandingsExportSections(sections, sectionKey, division, search, classification = "all", participation = "all") {
  const query = search.trim().toLowerCase();
  return sections.filter(section => sectionKey === "all" || section.key === sectionKey).map(section => {
    const activeDivision = section.rows.some(row => row.division === division) ? division : "all";
    return { ...section,
      rows: resultStandings(section.rows, "bullseye", { division: activeDivision, classification, participation }).filter(row => !query || row.name.toLowerCase().includes(query)),
      filterLabel: [activeDivision !== "all" && `Division: ${activeDivision}`, classification !== "all" && `Class: ${classification}`,
        participation !== "all" && `Participation: ${participation}`, query && `Search: ${search.trim()}`].filter(Boolean).join(" - "),
    };
  }).filter(section => section.rows.length);
}

function LiveExportIncludes({ classification, shots, onClassificationChange, onShotsChange, disabled }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  const id = useId();
  const count = Number(classification) + Number(shots);
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = event => { if (!root.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", dismiss, true);
    return () => document.removeEventListener("pointerdown", dismiss, true);
  }, [open]);
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  return <div ref={root} className="live-export-includes" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
  }} onKeyDown={event => {
    if (event.key === "Escape" && open) {
      event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.current?.focus();
    }
  }}>
    <button ref={trigger} type="button" className="live-export-includes-trigger" aria-label="Include optional fields"
      aria-expanded={open} aria-controls={open ? id : undefined} disabled={disabled} onClick={() => setOpen(value => !value)}>
      <span>Include{count ? ` (${count})` : ""}</span><ChevronDown size={14} aria-hidden="true" />
    </button>
    {open && <div id={id} role="group" aria-label="Optional PDF fields" className="live-export-includes-menu">
      <label><input type="checkbox" checked={classification} onChange={event => onClassificationChange(event.target.checked)} />Classification</label>
      <label><input type="checkbox" checked={shots} onChange={event => onShotsChange(event.target.checked)} />Shots</label>
    </div>}
  </div>;
}

export default function LiveStandingsExport({ match, sections, currentKey, division = "all", classification = "all", participation = "all", search = "", loading, error }) {
  const [scope, setScope] = useState(currentKey);
  const [includeClassification, setIncludeClassification] = useState(false);
  const [includeShots, setIncludeShots] = useState(false);
  const [useFilters, setUseFilters] = useState(true);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState("");
  const busy = useRef(false);
  useEffect(() => { setScope(currentKey); setFailure(""); }, [match.matchId, currentKey]);
  useEffect(() => { setIncludeClassification(false); setIncludeShots(false); }, [match.matchId]);
  const selectedKey = scope === "all" || sections.some(section => section.key === scope) ? scope : currentKey;
  const filterDescription = [division !== "all" && division, classification !== "all" && classification, participation !== "all" && participation, search.trim() && `Search: ${search.trim()}`].filter(Boolean).join(" · ");
  const selected = useMemo(() => liveStandingsExportSections(sections, selectedKey, useFilters ? division : "all", useFilters ? search : "", useFilters ? classification : "all", useFilters ? participation : "all"), [sections, selectedKey, division, search, classification, participation, useFilters]);
  const hasScores = selected.some(section => section.rows.some(row => row.scoreLabel && !["-", "—"].includes(row.scoreLabel)));
  async function download() {
    if (busy.current || loading || error || !hasScores) return;
    busy.current = true; setPending(true); setFailure("");
    try {
      const { downloadBullseyeLiveStandingsPdf } = await import("../../lib/bullseyeBulletinPdf");
      await downloadBullseyeLiveStandingsPdf({ matchId: match.matchId, matchStatus: match.status, name: match.matchName,
        date: matchDateLabel(match), location: matchLocation(match), sections: selected, includeClassification, includeShots });
    } catch (_) { setFailure("PDF couldn't download. Please try again."); }
    finally { busy.current = false; setPending(false); }
  }
  return <div className="live-export" aria-label="Export live standings">
    <p className="match-download-summary">{selectedKey === "all" ? "All score sections" : sections.find(section => section.key === selectedKey)?.label} · {useFilters && filterDescription ? filterDescription : "All competitors"} · PDF</p>
    {filterDescription && <label className="match-download-filter"><input type="checkbox" checked={useFilters} onChange={event => setUseFilters(event.target.checked)} />Use current page filters</label>}
    <div className="live-export-label">PDF section</div>
    <div className="live-export-controls">
      <DiscoverySelect label="PDF section" value={selectedKey} options={[{ value: "all", label: "All score sections" }, ...sections.map(section => ({ value: section.key, label: section.label }))]} onChange={setScope} />
      <LiveExportIncludes classification={includeClassification} shots={includeShots} onClassificationChange={setIncludeClassification} onShotsChange={setIncludeShots} disabled={pending} />
      <button type="button" className="live-refresh" disabled={pending || loading || Boolean(error) || !hasScores} onClick={download}>
        {pending ? <LoaderCircle size={16} aria-hidden="true" className="live-export-spinner" /> : <Download size={16} aria-hidden="true" />}{pending ? "Preparing PDF..." : "Download Standings PDF"}
      </button>
    </div>
    {failure && <p role="alert">{failure}</p>}
    {pending && <span className="sr-only" role="status">Preparing standings PDF</span>}
  </div>;
}
